import type { components } from "@contracts/generated/editor-service";

import { splitPartKey, type DraftPart, type PartKind } from "./assetContent";

/**
 * The named-document projection of a parts-shaped asset.
 *
 * Canonical family-specific editors present stored parts as named documents and
 * never show a part key, role vocabulary, or language prefix. This module is
 * the pure translation used by those editors. BG sets deliberately do not use
 * it: each domain is now its own independent Clinical Library asset and Project
 * adoption (2026-08-28-independent-bg-set-adoptions.md).
 *
 * WHAT THIS MODULE MAY KNOW, AND WHAT IT MUST NOT.
 *
 * The ROSTER of roles is the service's, fetched from `/v1/library/part-manifest`
 * (#491) and passed in. Nothing here enumerates the roles a type has, because a
 * second roster in the browser is free to drift from the one that actually
 * decides — the failure `services/editor-service/src/editor_service/library.py:163-172`
 * describes and #300 spent a ticket deleting.
 *
 * What IS here is product COPY keyed by role: the name a clinician reads for a
 * document the service names `prompt`. That is presentation, not a roster, and
 * it follows the discipline `lib/archiveRefusal.ts` established for the same
 * shape of problem — a PARTIAL map over server vocabulary plus a fallback that
 * keeps an unmapped member renderable. A role the service adds tomorrow gets a
 * humanised name rather than a blank row or a crash.
 *
 * AND WHAT IT DELIBERATELY DOES NOT KNOW: what is INSIDE a `steps`, `schema`,
 * or `diagnosis` document. `docs/superpowers/specs/2026-08-21-semantic-editor-fidelity-disposition.md:41-47`
 * rules that out — "a form that typed the inside of a `steps`, `schema` or
 * `domains` document would be asserting a schema that no authority in this
 * repository publishes" — and `schemas/library.py:143-157` keeps
 * `StructuredPart.data` as an unconstrained `JsonValue` for the same reason.
 */

export type LibraryPartDefinition =
  components["schemas"]["LibraryPartDefinition"];
export type LibraryAssetPartManifest =
  components["schemas"]["LibraryAssetPartManifest"];
export type LibraryPartManifest = components["schemas"]["LibraryPartManifest"];
export type LibraryAssetType = components["schemas"]["LibraryAssetType"];

/**
 * Product copy for a document the service names by role, keyed
 * `<assetType>.<role>`.
 *
 * PARTIAL BY CONSTRUCTION, with `humaniseRole` behind it. Only entries whose
 * product name genuinely differs from the humanised role are worth stating, and
 * every one below is evidenced rather than chosen:
 *
 * `main_concern.prompt` is "Chief complaint" because the file it mirrors is the
 * one this repository's own parity lane calls exactly that —
 * `CHIEF_COMPLAINT_RELPATH = "conversation_agent/en/init_track/main_concern/main_concern/main_concern.txt"`
 * (`services/editor-service/tests/test_sections_parity_against_real_brain_checkout.py:488`)
 * — and it is the label the canonical row carries.
 *
 * This is NOT the place to name a role the product has no name for. A humanised
 * role is an honest label; an invented one is a claim.
 */
const ROLE_PRODUCT_NAMES: Record<string, string> = {
  "main_concern.prompt": "Chief complaint",
  // The shared BG interview profile's three parts (#914). Each name is the
  // engine's own use of the document rather than a friendlier spelling of the
  // role: `humaniseRole` would render `tone` as "Tone", which says nothing
  // about a document that also carries both openings and every transition, and
  // `conversation_prompt` as "Conversation prompt", which is right but reads as
  // one of several conversations rather than THE prompt every background turn
  // of this profile is run under.
  "background_profile.tone": "Tone and openings",
  "background_profile.personality": "Personality",
  "background_profile.conversation_prompt": "Conversation prompt",
};

/**
 * What a required PROMPT document holds at v1 when nobody has written it yet.
 *
 * THE CANONICAL CREATION JOURNEY ASKS FOR A NAME, and the content is authored
 * afterwards in the named editor. This repository's service will not accept
 * that literally: `main_concern` requires a `prompt` document
 * (`services/editor-service/src/editor_service/library.py:176-179`) and
 * `PromptPart.text` carries `min_length=1`
 * (`services/editor-service/src/editor_service/schemas/library.py:135`), so a
 * v1 with no prompt text is refused. The three ways out are asking the
 * clinician for a prompt during creation — which is the field #492's accepted
 * criterion and the canonical dialog both exclude — deriving one from the name,
 * which would put a title where a clinical instruction belongs, or supplying a
 * placeholder that says what it is.
 *
 * This is the third. It asserts nothing clinical, it reads as unfinished on
 * every surface that shows it, and the clinician replaces it in the document's
 * own editor — which is the post-create step the canonical journey describes.
 * Contrast the structured half: a required structured document opens on `{}`,
 * for the same reason and with the same honesty.
 */
export const UNWRITTEN_PROMPT_TEXT =
  "This prompt has not been written yet. Open it and replace this text.";

/** `screening_prompt` -> `Screening prompt`. The fallback under the copy map. */
export function humaniseRole(role: string): string {
  const spaced = role.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The clinician-facing name of the document this role holds. */
export function roleProductName(
  assetType: LibraryAssetType | string,
  role: string,
): string {
  return ROLE_PRODUCT_NAMES[`${assetType}.${role}`] ?? humaniseRole(role);
}

/** This type's entry in the service's manifest, or `null` when it has none. */
export function manifestFor(
  manifest: LibraryPartManifest | undefined,
  assetType: LibraryAssetType | string,
): LibraryAssetPartManifest | null {
  return (
    manifest?.items.find((entry) => entry.assetType === assetType) ?? null
  );
}

/**
 * Every role the service names for a type, in ITS declaration order.
 *
 * Required families first — flattened, because a type with alternative families
 * (`pathway_variant`) offers every role any family names, and which family a
 * given asset satisfies is `validate_parts`' answer, not this module's. Allowed
 * parts follow. Deduped by role, keeping the first kind seen.
 */
export function manifestRoles(
  entry: LibraryAssetPartManifest | null,
): LibraryPartDefinition[] {
  if (entry === null) {
    return [];
  }
  const seen = new Set<string>();
  const roles: LibraryPartDefinition[] = [];
  for (const definition of [
    ...entry.requiredFamilies.flat(),
    ...entry.allowedParts,
  ]) {
    if (seen.has(definition.role)) {
      continue;
    }
    seen.add(definition.role);
    roles.push(definition);
  }
  return roles;
}

/**
 * The roles of the FIRST required family, which is what a new asset must carry.
 *
 * The first rather than a merged superset: a type with alternatives requires one
 * family satisfied COMPLETELY (`library.py`'s `pathway_variant` note), so
 * seeding a new asset from the union would produce content no family accepts.
 */
export function requiredRoles(
  entry: LibraryAssetPartManifest | null,
): LibraryPartDefinition[] {
  return entry?.requiredFamilies[0] ?? [];
}

/** The kind the service declares for a role, or `null` when it names none. */
export function manifestKind(
  entry: LibraryAssetPartManifest | null,
  role: string,
): PartKind | null {
  const found = manifestRoles(entry).find(
    (definition) => definition.role === role,
  );
  return found ? (found.kind as PartKind) : null;
}

/** One stored document inside a named row: a single part, minus its key. */
export interface NamedDocument {
  /** `DraftPart.id` — identity for React and for edits, never the key. */
  id: string;
  /** The key's language prefix, or `null` for a language-neutral document. */
  language: string | null;
  kind: PartKind;
  text: string;
}

/**
 * One named document family on the surface: a role, and every language of it.
 *
 * ROWS ARE ROLES, NOT PARTS, and that is the load-bearing decision in this
 * module. This repository's assets are genuinely bilingual — "Part keys are
 * language-prefixed (`he/screening_prompt`)" and the seeded main concern carries
 * five roles in Hebrew and two in English
 * (`services/editor-service/src/editor_service/seed/manifest.yaml:9-14`, `:23-33`) —
 * while the canonical reference has no language concept at all. Rows keyed on the
 * PART would therefore show "Chief complaint" twice with nothing to tell them
 * apart, which is worse than either alternative: a clinician would edit whichever
 * one they happened to click. Keyed on the ROLE, the surface shows the document
 * the product names once, and its languages are inside it — a read-only fact
 * about the content, never a control that authors a prefix.
 */
export interface NamedRow {
  role: string;
  kind: PartKind;
  /** The product name a clinician reads. */
  label: string;
  /** Every stored document for this role, in the order the parts carry. */
  documents: NamedDocument[];
}

/**
 * The asset's parts as named rows, in authored order.
 *
 * Order is content — `parts` is an ordered list by contract — so the rows come
 * out in the order the roles FIRST appear, and reordering them below rewrites
 * the parts array to match.
 *
 * The kind is taken from the stored part rather than from the manifest: what is
 * on screen must describe what is actually stored, and if the two ever disagree
 * the server's refusal is the thing that resolves it.
 */
export function namedRows(
  parts: readonly DraftPart[],
  assetType: LibraryAssetType | string,
): NamedRow[] {
  const byRole = new Map<string, NamedRow>();
  for (const part of parts) {
    const [language, role] = splitPartKey(part.key);
    const document: NamedDocument = {
      id: part.id,
      language,
      kind: part.kind,
      text: part.text,
    };
    const existing = byRole.get(role);
    if (existing) {
      existing.documents.push(document);
      continue;
    }
    byRole.set(role, {
      role,
      kind: part.kind,
      label: roleProductName(assetType, role),
      documents: [document],
    });
  }
  return [...byRole.values()];
}

/** The row for one role, or `null`. */
export function findNamedRow(
  rows: readonly NamedRow[],
  role: string,
): NamedRow | null {
  return rows.find((row) => row.role === role) ?? null;
}

/** How many named rows hold prompt documents — the canonical "N prompts". */
export function promptRowCount(rows: readonly NamedRow[]): number {
  return rows.filter((row) => row.kind === "prompt_text").length;
}

/**
 * The languages this asset's parts carry, in first-appearance order.
 *
 * `null` is a real member: a bare key is language-NEUTRAL rather than a defaulted
 * language, exactly as `splitPartKey` and `library.split_part_key` read it.
 */
export function languagesInParts(
  parts: readonly DraftPart[],
): (string | null)[] {
  const seen: (string | null)[] = [];
  for (const part of parts) {
    const [language] = splitPartKey(part.key);
    if (!seen.includes(language)) {
      seen.push(language);
    }
  }
  return seen;
}

/** `he` + `prompt` -> `he/prompt`; a neutral language -> `prompt`. */
export function joinPartKey(language: string | null, role: string): string {
  return language === null ? role : `${language}/${role}`;
}

/** One document's text replaced, everything else untouched. */
export function updateNamedDocument(
  parts: readonly DraftPart[],
  documentId: string,
  text: string,
): DraftPart[] {
  return parts.map((part) =>
    part.id === documentId ? { ...part, text } : part,
  );
}

/** Every document of one role removed. */
export function removeNamedRow(
  parts: readonly DraftPart[],
  role: string,
): DraftPart[] {
  return parts.filter((part) => splitPartKey(part.key)[1] !== role);
}

/**
 * One named row moved, carrying its languages with it.
 *
 * Rebuilt from the role order rather than spliced, because a role's documents
 * are not necessarily adjacent in the stored array — the seed sorts parts by key,
 * so `he/prompt` and `en/prompt` sit in different halves of it. Moving a row
 * therefore groups each role's documents as it goes, which is a real change to
 * the stored order and produces a new version exactly as any other edit does.
 */
export function moveNamedRow(
  parts: readonly DraftPart[],
  role: string,
  to: number,
): DraftPart[] {
  const order = namedRows(parts, "").map((row) => row.role);
  const from = order.indexOf(role);
  if (from === -1 || to < 0 || to >= order.length || from === to) {
    return [...parts];
  }
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, role);

  return next.flatMap((each) =>
    parts.filter((part) => splitPartKey(part.key)[1] === each),
  );
}

/**
 * A named role added, once per language the asset already carries.
 *
 * The languages come from the asset rather than from a control: the canonical
 * surface has no language concept, and inventing one here would be the Language
 * prefix control the 2026-08-25 amendment forbids. An asset carrying no parts
 * yet gets one language-neutral document, which is a legal key and the only
 * honest answer when nothing on the asset says which languages it serves.
 *
 * A structured document opens on `{}` for the reason `newDraftPart` states — ""
 * is not JSON — and a prompt document opens on `initialText`.
 *
 * `initialText` DEFAULTS TO EMPTY, AND THAT DEFAULT IS ONLY SAFE INSIDE A DRAFT.
 * An empty prompt is refused by `PromptPart.text`'s `minLength: 1`, so a row
 * added into an open draft opens blank and `useNamedDraft`'s Save reports "A
 * prompt part cannot be empty." until the clinician fills it — a stated refusal,
 * on a control they are already looking at.
 *
 * A CALLER THAT PUBLISHES IMMEDIATELY HAS NO SUCH MOMENT. An immediate publish
 * with an empty prompt makes
 * `toPartsContent` refuse before a request was ever built: no message, no
 * network call, nothing on screen. That was Manual QA's D1 — a Create button
 * that did nothing at all. Such a caller passes `UNWRITTEN_PROMPT_TEXT`, the
 * same authoritative initial content `NewMainConcernDialog` gives a main
 * concern's required documents, and for exactly the same reason.
 */
export function addNamedRole(
  parts: readonly DraftPart[],
  role: string,
  kind: PartKind,
  nextId: () => string,
  initialText = "",
): DraftPart[] {
  const languages = languagesInParts(parts);
  const targets = languages.length === 0 ? [null] : languages;
  return [
    ...parts,
    ...targets.map((language) => ({
      id: nextId(),
      key: joinPartKey(language, role),
      kind,
      text: kind === "structured" ? "{}" : initialText,
    })),
  ];
}

/**
 * The required languages this asset does not yet carry, in the server's order.
 *
 * `required` IS THE SERVER'S LIST and this module never guesses at it: it is
 * `FamilyPolicy.languages.required` off `GET /v1/library/placement-registry`,
 * the same document the write path's own gate reads
 * (`library.validate_required_languages`). That is what keeps this from being
 * the Language prefix control the 2026-08-25 amendment forbids — the browser
 * offers a language the SERVICE says a Rule may not omit, never a free-text
 * prefix a clinician invents.
 *
 * A language-neutral document does not count as coverage, exactly as the gate
 * reads it: every path a bilingual family composes to is `{lang}`-templated, so
 * a bare key can never place at either required language.
 */
export function missingRequiredLanguages(
  parts: readonly { key: string }[],
  required: readonly string[],
): string[] {
  const carried = new Set(
    parts
      .map((part) => splitPartKey(part.key)[0])
      .filter((language): language is string => language !== null),
  );
  return required.filter((language) => !carried.has(language));
}

/**
 * Every named role of this asset copied into one language it is missing.
 *
 * The mirror image of `addNamedRole`, which adds one ROLE across the languages
 * the asset already carries: this adds one LANGUAGE across the roles it already
 * carries. Together they are the two axes of a bilingual parts envelope, and
 * before #635 only the first had a control — which is why a Rule authored in
 * English could be published, refused at nothing, and never repaired on screen.
 *
 * Roles come from the asset rather than from the manifest, for the reason this
 * module states at the top: the roster is the service's, and a new language
 * should mirror what this asset actually says rather than what its family
 * allows. A role already present at `language` is left alone, so calling this
 * twice adds nothing the second time.
 *
 * `initialText` carries the same warning `addNamedRole`'s does: an empty prompt
 * is refused by `PromptPart.text`'s `minLength: 1`, which inside an open draft
 * is a stated refusal on a control the clinician is looking at — and for a
 * caller that publishes immediately is nothing on screen at all. Such a caller
 * passes `UNWRITTEN_PROMPT_TEXT`.
 */
export function addRequiredLanguage(
  parts: readonly DraftPart[],
  language: string,
  nextId: () => string,
  initialText = "",
): DraftPart[] {
  const present = new Set(
    parts
      .filter((part) => splitPartKey(part.key)[0] === language)
      .map((part) => splitPartKey(part.key)[1]),
  );
  const added: DraftPart[] = [];
  for (const row of namedRows(parts, "")) {
    if (present.has(row.role)) {
      continue;
    }
    present.add(row.role);
    added.push({
      id: nextId(),
      key: joinPartKey(language, row.role),
      kind: row.kind,
      text: row.kind === "structured" ? "{}" : initialText,
    });
  }
  return [...parts, ...added];
}

/**
 * The roles this content may NOT drop, given what it currently carries.
 *
 * `library.validate_parts` refuses a version that satisfies no required family,
 * and `requiredFamilies` is a list of ALTERNATIVES rather than one flat list: a
 * `pathway_variant` is complete with `[classification, diagnosis,
 * full_evaluation_schema]` OR with `[screening_prompt, screening_steps]`.
 * Flattening it would refuse the removal of a role the asset does not actually
 * depend on — a control disabled for a rule that does not apply — so the
 * question asked here is the one the service asks: would dropping this role
 * leave EVERY family incomplete?
 *
 * An unread manifest yields an empty set, and the caller says so rather than
 * inferring a rule from a roster it does not have.
 */
export function unremovableRoles(
  entry: LibraryAssetPartManifest | null,
  present: Iterable<string>,
): Set<string> {
  const families = entry?.requiredFamilies ?? [];
  const roles = new Set(present);
  if (families.length === 0) {
    return new Set();
  }
  return new Set(
    [...roles].filter((role) => {
      const after = new Set([...roles].filter((other) => other !== role));
      return !families.some((family) =>
        family.every((definition) => after.has(definition.role)),
      );
    }),
  );
}

/**
 * The named roles the service allows on this type that the asset does not carry.
 *
 * What "Add prompt" offers, and the reason it does not need a
 * free-text key: the roster is the manifest's, the asset says which of it is
 * already used, and the difference is a short list of concrete documents rather
 * than a vocabulary a clinician has to know.
 */
export function addableRoles(
  entry: LibraryAssetPartManifest | null,
  /**
   * Anything carrying part keys — a draft, or the stored wire parts.
   *
   * Widened from `DraftPart[]` deliberately: a caller that only wants to know
   * which roles are FREE should not have to convert stored content into a draft
   * first, because `toDraft` mints identities from a module counter and doing
   * that during render burns ids on drafts nobody edits.
   */
  parts: readonly { key: string }[],
  kind?: PartKind,
): LibraryPartDefinition[] {
  const present = new Set(
    parts.map((part) => splitPartKey(part.key)[1]),
  );
  return manifestRoles(entry).filter(
    (definition) =>
      !present.has(definition.role) &&
      (kind === undefined || definition.kind === kind),
  );
}
